//Dealer Franchisee subclass views
import {
  combineStatesAsList,
  flagListEquals,
  isEmptyOrSpace
} from '../../../../webmodule-common/other/ui/string-helper-functions';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { createNewQuote } from '../quote-creator';
import { customElement, state } from 'lit/decorators.js';
import { defaultStagedThreshold, EventCancellation } from '../../../../webmodule-common/other/general/staged-event';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services';
import { EventSnippet, NullPromise } from '../../../../webmodule-common/other/ui/events';

import { FranchiseeApi } from '../../../api/franchisee-api';
import { FranchiseeQuoteSummaryTable } from './franchisee-quote-summary-table';
import { FranchiseeV6BranchQuoteSupplierDefaultsView } from './v6/franchisee-v6-branchdefaults';
import { getApiFactory } from '../../../api/api-injector';

import { getQuoteContainer, getQuoteContainerManager } from '../quote-ui-adapter';
import { getSupplier } from '../supplier-selector';
import { html } from 'lit';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';

import { notificationSignal } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { PageManager } from '../../../../webmodule-common/other/ui/page-control';
import { QuoteContainer, QuoteContainerManager } from '../../../quotes/data/quote-container';
import { QuoteListView, QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import { QuoteState, ViewQuoteSummary, ViewQuoteTotalByState } from '../../../api/dealer-api-interface-quote';
import { SSEQuote } from '../../../quotes/data/sse-quote';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';
import { userDataStore } from '../../common/current-user-data-store';
import { WMEventSource } from '../../../api/event-source';

const numberOfDaysHistory = 30;

@customElement('wm-franchiseequotelistview')
export class FranchiseeQuoteListView extends QuoteListView {
  franchiseeApi: FranchiseeApi = getApiFactory().franchisee();
  @state()
  quoteTotals: ViewQuoteTotalByState[] | null = null;
  private staleQuotes: string[] = [];

  @state()
  quoteDefaultsTab = true;
  @state()
  quoteDefaultsUI?: FranchiseeV6BranchQuoteSupplierDefaultsView;
  async init() {
    await this.updatePageTotals();
    const table = this.pageControl.activePage?.data as QuoteSummaryTable;
    if (table) await table.refreshData(1);
  }

  async doAfterQuoteEdit() {
    await this.updatePageTotals();
  }

  private _eventCancel: EventCancellation = new EventCancellation();
  private _eventQuoteUpdated = stagedExecution({
    threshold: defaultStagedThreshold,
    cancelToken: this._eventCancel,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      this.updatePageTotals();
      this.tables.forEach(t => (t.dataIsStale = true));
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      this.staleQuotes = [];
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
      //reload the page.. we dont know what moved, or where, or what state its in
    },
    eventTriggered: (_data: SSEQuote) => {
      notificationSignal(true);
    },
    eventActioned: async (data: SSEQuote) => {
      if (!this.staleQuotes.includes(data.id)) this.staleQuotes.push(data.id);
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
      }
      // Copy completion toast now handled globally via registerQuoteCopyCompletionMonitor
    }
  });

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return getQuoteContainerManager(quoteContainer);
  }

  protected async updatePageTotals() {
    const ownerId = await this.quoteOwnerId();
    const totals = await this.quoteApi.getQuoteTotalsByState({
      quoteOwnerId: [ownerId],
      options: [
        {
          state: combineStatesAsList(
            QuoteState.Draft,
            QuoteState.Active,
            QuoteState.Active + QuoteState.Copying,
            QuoteState.Draft + QuoteState.Copying
          ),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Draft, QuoteState.Draft + QuoteState.Copying),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Active, QuoteState.Active + QuoteState.Copying),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Issued, QuoteState.IssuePending),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Accepted),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.Accepted_AssignedToReviewer, QuoteState.SupplierReviewPending),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(QuoteState.SupplierReviewed),
          numberOfDaysHistory: null
        },
        {
          state: combineStatesAsList(
            QuoteState.Approved,
            QuoteState.Rejected,
            QuoteState.Accepted_RefusedBySupplier,
            QuoteState.Cancelled,
            QuoteState.Lapsed
          ),
          numberOfDaysHistory: numberOfDaysHistory
        }
      ]
    });
    if (totals) this.quoteTotals = totals.totals.filter(x => x.quoteOwnerId === ownerId);
    this.pageControl.requestUpdate();
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return getQuoteContainer(quoteId);
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): FranchiseeQuoteSummaryTable {
    return new FranchiseeQuoteSummaryTable(options);
  }

  beforeRefresh() {
    this.staleQuotes = [];
  }

  afterRefresh() {
    this._eventCancel.cancel();
  }

  async dispose(): Promise<void> {
    await super.dispose();
    this._eventCancel.cancel();
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState[],
    pageFragment: string,
    numberOfDays?: number
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDays,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      pageFragment: pageFragment,
      stale: { exists: (id: string) => this.staleQuotes.includes(id) }
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    const title = (caption: string, state: QuoteState[]) => {
      const count =
        this.quoteTotals?.filter(x => flagListEquals(state, x.quoteState)).reduce((sum, x) => sum + x.quoteCount, 0) ??
        0;
      return html`${caption} (${count})`;
    };
    const makeTable = (titleStr: () => string, state: QuoteState[], pageFragment: string, numberOfDays?: number) => {
      return this.createSummaryTable(() => title(titleStr(), state), state, pageFragment, numberOfDays);
    };

    return [
      makeTable(
        () => LangFranchiseeQuoteListView.allCurrentTableTitle(),
        combineStatesAsList(
          QuoteState.Draft,
          QuoteState.Active,
          QuoteState.Copying + QuoteState.Draft,
          QuoteState.Copying + QuoteState.Active
        ),
        'allCurrent',
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.draftTableTitle(),
        combineStatesAsList(QuoteState.Draft, QuoteState.Draft + QuoteState.Copying),
        'draft',
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.activeTableTitle(),
        combineStatesAsList(QuoteState.Active, QuoteState.Active + QuoteState.Copying),
        'active',
        undefined
      ),

      makeTable(
        () => LangFranchiseeQuoteListView.issuedTableTitle(),
        combineStatesAsList(QuoteState.Issued, QuoteState.IssuePending),
        'issued',
        undefined
      ),

      makeTable(
        () => LangFranchiseeQuoteListView.acceptedTableTitle(),
        combineStatesAsList(QuoteState.Accepted),
        'accepted',
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.underReviewTableTitle(),
        combineStatesAsList(QuoteState.Accepted_AssignedToReviewer, QuoteState.SupplierReviewPending),
        'underreview',
        undefined
      ),
      makeTable(
        () => LangFranchiseeQuoteListView.reviewedTableTitle(),
        combineStatesAsList(QuoteState.SupplierReviewed),
        'reviewed',
        undefined
      ),

      makeTable(
        () => LangFranchiseeQuoteListView.archivedTableTitle(),
        combineStatesAsList(
          QuoteState.Approved,
          QuoteState.Rejected,
          QuoteState.Accepted_RefusedBySupplier,
          QuoteState.Cancelled,
          QuoteState.Lapsed
        ),
        'archived',
        numberOfDaysHistory
      )
    ];
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return null;
    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return null;
    }
    const owner = await this.quoteOwnerId();

    return createNewQuote({ supplierId: supplier.supplierId, owner: owner });
  }

  protected getInitialPageManagers(): PageManager[] {
    const results = super.getInitialPageManagers();
    results.push(this.makeQuoteDefaultsPageManager());
    return results;
  }
  makeQuoteDefaultsPageManager(): PageManager {
    const pm: PageManager = {
      caption: () => LangFranchiseeQuoteListView.quoteDefaultsTab(),
      canClose: () => Promise.resolve(false),
      canLeave: async () => {
        return (await this.quoteDefaultsUI?.saveChanges()) ?? true;
      },
      hasDelete: () => false,
      onEnter: async () => {
        this.quoteDefaultsUI ??= await constructAsync(new FranchiseeV6BranchQuoteSupplierDefaultsView());

        //we need to load the defaults data from the server.
      },
      content: () => {
        return html`${this.quoteDefaultsUI}`;
      },
      data: undefined,
      pageFragment: 'quotedefaults',
      buttonMenu: () => this.quoteDefaultsUI?.buttonMenu() ?? ''
    };
    return pm;
  }
}
